import React, { useEffect, useState } from 'react';
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import { Heading, Text } from "../../../components/DesignSystem/components/Typography";
import { Button as DSButton } from "../../../components/DesignSystem/components/Button";
import { ArrowLeft, ArrowRight, X, Plus, Lightbulb, Sun, Smile, CameraOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useRef, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import ImageCrop from "../../../components/cropimage/ImageCrop";
import * as userActions from "../../../actions/user";
import { fileUpload } from "../../../actions/uploadActions";
import { useAlert } from "../../../Context/AlertModalContext";
import { getProfileSelfieImageUrls } from "../../../utils/serverUtils";

export const PhotoUpload = () => {
    const navigate = useNavigate();
    
    // 1. Theme Configuration Hook
    const theme = useTheme();
    const mode = theme.palette.mode;
    
    // 2. FOUC Tracker State
    const [isTailwindLoaded, setIsTailwindLoaded] = useState(!!window.tailwind);

    const alert = useAlert();
    const [loading, setLoading] = useState(false);
    const [duplicateImageError, setDuplicateImageError] = useState(false);

    const selectedFileHashesRef = useRef({});
    const fileInputRefs = {
        main: useRef(null),
        first: useRef(null),
        second: useRef(null),
    };

    const [images, setImages] = useState({
        main: { key: null, src: null, fileName: null, loading: 0, imageKey: "main", file: null, actionType: null },
        first: { key: null, src: null, fileName: null, loading: 0, imageKey: "first", file: null, actionType: null },
        second: { key: null, src: null, fileName: null, loading: 0, imageKey: "second", file: null, actionType: null },
    });

    const computeHash = async (file) => {
        try {
            const arrayBuffer = await file.arrayBuffer();
            const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
        } catch (error) {
            return `${file.name}-${file.size}-${file.lastModified}`;
        }
    };

    const validateFile = (file) => {
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            throw new Error("Please select a valid image file (JPEG, PNG, or WebP)");
        }
        return true;
    };

    const cropperRef = useRef(null);
    const [imageToCrop, setImageToCrop] = useState(null);
    const [showCropModal, setShowCropModal] = useState(false);
    const [currentImageKey, setCurrentImageKey] = useState(null);

    const IMAGE_CROP_DIMENSIONS = {
        main: { width: 600, height: 800 },
        first: { width: 600, height: 800 },
        second: { width: 600, height: 800 },
    };
    const { width, height } = IMAGE_CROP_DIMENSIONS[currentImageKey] || { width: 600, height: 800 };

    useEffect(() => {
        const fetchUserImage = async () => {
            try {
                const response = await userActions.getUserMedia();
                if (response) {
                    const profileImages = await getProfileSelfieImageUrls(response);
                    setImages((prev) => {
                        const updatedImages = { ...prev };
                        Object.entries(profileImages).forEach(([key, url]) => {
                            if (url) {
                                updatedImages[key] = {
                                    ...updatedImages[key],
                                    src: url,
                                    actionType: "uploaded"
                                };
                            }
                        });
                        return updatedImages;
                    });
                }
            } catch (error) {
                console.error("Error fetching user images:", error);
            }
        };
        fetchUserImage();
    }, []);

    const handleClick = imageKey => {
        const input = fileInputRefs[imageKey]?.current;
        if (input) {
            input.value = "";
            input.click();
        }
    };

    const handleFileChange = async (e, imageKey) => {
        const files = e.target?.files;
        if (!files?.length) return;
        const file = files[0];

        try {
            validateFile(file);
            const fileHash = await computeHash(file);
            const isDuplicate = Object.entries(selectedFileHashesRef.current).some(
                ([key, hash]) => key !== imageKey && hash === fileHash
            );

            if (isDuplicate) {
                setDuplicateImageError(true);
                return;
            }

            setDuplicateImageError(false);
            selectedFileHashesRef.current[imageKey] = fileHash;

            const reader = new FileReader();
            reader.onload = () => {
                setImageToCrop(reader.result);
            };
            reader.onerror = () => {
                throw new Error("Failed to read image file");
            };
            reader.readAsDataURL(file);

            setCurrentImageKey(imageKey);
            setShowCropModal(true);
        } catch (error) {
            alert.setModal({
                show: true,
                title: "Invalid File",
                message: error.message || "Please select a valid image file.",
            });
            e.target.value = "";
        }
    };

    const handleCropCancel = () => {
        setCurrentImageKey(null);
        setShowCropModal(false);
        setImageToCrop(null);
        if (currentImageKey && fileInputRefs[currentImageKey]?.current) {
            fileInputRefs[currentImageKey].current.value = "";
        }
    };

    const handleCropSave = async () => {
        try {
            if (!cropperRef.current) throw new Error("Cropper not initialized");

            const croppedBlob = await cropperRef.current.handleSubmit();
            if (!croppedBlob) throw new Error("Failed to crop image. Please try again.");

            const croppedFile = new File([croppedBlob], "cropped-image.jpg", {
                type: "image/jpeg",
            });

            const fileURL = URL.createObjectURL(croppedFile);

            setImages((prev) => ({
                ...prev,
                [currentImageKey]: {
                    ...prev[currentImageKey],
                    croppedFile,
                    fileName: croppedFile.name,
                    src: fileURL,
                    loading: -1,
                },
            }));

            handleCropCancel();

            try {
                const uploadedFile = await fileUpload(croppedFile);
                setImages((prev) => ({
                    ...prev,
                    [currentImageKey]: {
                        ...prev[currentImageKey],
                        key: uploadedFile.key,
                        loading: 0,
                    },
                }));
            } catch (error) {
                setImages((prev) => ({
                    ...prev,
                    [currentImageKey]: {
                        ...prev[currentImageKey],
                        loading: 0,
                    },
                }));
                alert.setModal({
                    show: true,
                    title: "Upload Failed",
                    message: error.message || "Failed to upload image. Please check your internet connection and try again.",
                });
            }
        } catch (error) {
            alert.setModal({
                show: true,
                title: "Crop Error",
                message: error.message || "Something went wrong while cropping. Please try again.",
            });
        }
    };

    const handleNextClick = async () => {
        const requiredKeys = ["main", "first", "second"];

        const isAnyImageMissing = requiredKeys.some((key) => {
            const image = images[key];
            const hasSrc = !!image?.src;
            const hasKey = !!image?.key;
            const isAlreadyUploaded = image?.actionType === "uploaded";
            return !hasSrc || (!hasKey && !isAlreadyUploaded);
        });

        if (isAnyImageMissing) {
            alert.setModal({
                show: true,
                title: "Missing Images",
                message: "Please upload all three images before continuing.",
            });
            return;
        }

        setLoading(true);

        const payload = {};
        requiredKeys.forEach((key) => {
            if (images[key]?.key) {
                payload[key] = images[key].key;
            }
        });

        // Navigate instantly for smooth UX
        navigate("/kids-family");

        try {
            if (Object.keys(payload).length > 0) {
                userActions.updateUserMedia(payload).catch((error) => {
                    console.error("Failed to save your images in background:", error);
                });
            }
        } catch (error) {
            console.error("Error executing update logic:", error);
        } finally {
            setLoading(false);
        }
    };

    const renderImageContent = (img, label) => (
        <>
            {!img.src ? (
                <div className="flex flex-col items-center justify-center h-full w-full p-2 text-center absolute inset-0 text-slate-400">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-brand/10 text-brand flex items-center justify-center mb-1 group-hover:scale-110 transition-transform">
                        <Plus strokeWidth={3} className="w-5 h-5 lg:w-6 lg:h-6" />
                    </div>
                    <span className="text-[10px] lg:text-[11px] font-bold uppercase tracking-wider">{label}</span>
                </div>
            ) : (
                <img
                    src={img.src}
                    className="absolute inset-0 w-full h-full object-cover scale-[1.02]"
                    alt="Uploaded"
                />
            )}

            {img.loading === -1 && (
                <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white z-10">
                    <Spinner animation="border" size="sm" />
                    <span className="text-[10px] uppercase mt-2 font-bold tracking-widest">Processing</span>
                </div>
            )}
        </>
    );

    // 3. Centralized FOUC Prevention & Engine Mount
    useEffect(() => {
        const applyConfig = () => {
            const modeColors = tokens(mode);
            if (window.tailwind) {
                window.tailwind.config = {
                    darkMode: "class",
                    theme: {
                        extend: {
                            colors: {
                                "brand": modeColors.greenAccent[500] || "#ff794d",
                                "background-light": mode === "dark" ? modeColors.primary[500] : "#f8f6f5",
                                "background-dark": modeColors.primary[500] || "#23140f",
                                "action-black": "#111111",
                            },
                            fontFamily: {
                                "display": ["Inter", "sans-serif"],
                                "body": ['"Source Sans Pro"', "sans-serif"],
                            },
                        },
                    },
                };
            }
        };

        if (window.tailwind) {
            applyConfig();
            setIsTailwindLoaded(true);
        } else {
            const script = document.createElement("script");
            script.src = "https://cdn.tailwindcss.com?plugins=forms";
            script.onload = () => {
                applyConfig();
                setIsTailwindLoaded(true);
            };
            document.head.appendChild(script);
        }
    }, [mode]);

    return (
        <div 
            className={`${mode === "dark" ? "dark" : ""} bg-background-light dark:bg-background-dark font-display text-slate-900 dark:text-slate-100 antialiased min-h-[100dvh] lg:h-screen lg:overflow-hidden w-full`}
            style={{ opacity: isTailwindLoaded ? 1 : 0, transition: 'opacity 0.1s ease-out' }}
        >
            <div className="flex flex-col lg:flex-row min-h-[100dvh] lg:h-screen w-full overflow-hidden">
                
                {/* ---------- LEFT SIDE: PRO TIP PANEL ---------- */}
                <aside className="hidden lg:flex lg:w-[40%] bg-white dark:bg-slate-900/50 border-r border-slate-200 dark:border-slate-800 p-4 lg:p-6 xl:p-10 flex-col justify-center z-10 h-screen overflow-hidden">
                    <div className="max-w-md mx-auto lg:mx-0 py-2 w-full flex flex-col justify-center h-full max-h-screen">
                        <Heading variant="h1" className="text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-black leading-tight mb-1 xl:mb-2 tracking-tight text-slate-900 dark:text-slate-100">
                            Make a Great <br/>
                            <span className="text-brand">First Impression</span>
                        </Heading>
                        <Text as="p" className="text-sm lg:text-base xl:text-lg text-slate-600 dark:text-slate-400 leading-snug xl:leading-relaxed mb-4 xl:mb-6">
                            Your photos are the first thing people see. 
                        </Text>
                        
                        <div className="space-y-3 xl:space-y-5">
                            {/* Tip 1 */}
                            <div className="flex gap-3 xl:gap-4 items-start xl:items-center">
                                <div className="flex-shrink-0 w-8 h-8 xl:w-12 xl:h-12 rounded-full bg-brand/10 flex items-center justify-center text-brand">
                                    <Sun className="w-4 h-4 xl:w-6 xl:h-6" />
                                </div>
                                <div>
                                    <Heading variant="h4" className="font-bold text-base xl:text-lg mb-0.5 text-slate-900 dark:text-slate-100">Great Lighting</Heading>
                                    <Text as="p" className="text-slate-500 dark:text-slate-400 text-xs xl:text-sm leading-tight">Natural daylight works best. Avoid harsh shadows or overly dark rooms.</Text>
                                </div>
                            </div>
                            {/* Tip 2 */}
                            <div className="flex gap-3 xl:gap-4 items-start xl:items-center">
                                <div className="flex-shrink-0 w-8 h-8 xl:w-12 xl:h-12 rounded-full bg-brand/10 flex items-center justify-center text-brand">
                                    <Smile className="w-4 h-4 xl:w-6 xl:h-6" />
                                </div>
                                <div>
                                    <Heading variant="h4" className="font-bold text-base xl:text-lg mb-0.5 text-slate-900 dark:text-slate-100">Friendly Smiles</Heading>
                                    <Text as="p" className="text-slate-500 dark:text-slate-400 text-xs xl:text-sm leading-tight">A warm, genuine smile makes you more approachable and professional.</Text>
                                </div>
                            </div>
                            {/* Tip 3 */}
                            <div className="flex gap-3 xl:gap-4 items-start xl:items-center">
                                <div className="flex-shrink-0 w-8 h-8 xl:w-12 xl:h-12 rounded-full bg-brand/10 flex items-center justify-center text-brand">
                                    <CameraOff className="w-4 h-4 xl:w-6 xl:h-6" />
                                </div>
                                <div>
                                    <Heading variant="h4" className="font-bold text-base xl:text-lg mb-0.5 text-slate-900 dark:text-slate-100">No Filters</Heading>
                                    <Text as="p" className="text-slate-500 dark:text-slate-400 text-xs xl:text-sm leading-tight">Avoid heavy filters or text overlays. Keep it clean and natural.</Text>
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 xl:mt-6 p-3 xl:p-4 bg-background-light dark:bg-background-dark/50 rounded-xl border border-brand/20 dark:border-brand/20 shrink-0">
                            <div className="flex items-center gap-3 xl:gap-4">
                                <div className="w-12 h-12 xl:w-16 xl:h-16 rounded-lg bg-cover bg-center shrink-0 border border-brand/20" style={{ backgroundImage: "url('https://lh3.googleusercontent.com/aida-public/AB6AXuB0A4qQZ4UF5IFto3RvGvB9xO5iFtreh1zr9tccnQqy6APNUjZSS8_q9XQn4hrZxugFpf_umwUO1g_MpLvI0jP_taLku--iDFoRHcR64mb5USu3S6NQ_iZtEEtG1BMEl9LGZs5KGJ4TW5WrKxgJDvDf6i5ADk6Lm70AN2IXbN0-fa7xzT8jHLKdIegn76GdcULq626YBQXU5W8UDpUzeDZTmrUq_GNfKOLUZR1aK45H8cdK_i1jOJR70bXlnpuiOfQA7RHkTJBMu6M2')" }}></div>
                                <div>
                                    <Text as="p" className="text-[10px] xl:text-xs font-bold text-brand uppercase">Example</Text>
                                    <Text as="p" className="text-xs xl:text-sm font-medium text-slate-900 dark:text-slate-100">Clear, centered, and bright.</Text>
                                </div>
                            </div>
                        </div>
                    </div>
                </aside>
                
                {/* ---------- RIGHT SIDE: CONTENT / FORM ---------- */}
                <div className="w-full lg:w-[60%] flex flex-col bg-white dark:bg-background-dark min-h-[100dvh] lg:h-screen overflow-y-auto custom-scrollbar relative">
                    
                    {/* Header/Nav */}
                    <header className="sticky top-0 z-30 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md px-[var(--ds-space-6)] py-[var(--ds-space-4)] flex items-center justify-between border-b border-brand/10">
                        <button type="button" onClick={() => navigate(-1)} className="flex items-center justify-center h-10 w-10 shrink-0 rounded-full bg-brand/10 text-action-black dark:text-white hover:bg-brand/20 transition-colors" aria-label="Go back">
                            <ArrowLeft className="shrink-0" size={24} />
                        </button>
                        <div className="flex items-center gap-[var(--ds-space-2)]">
                            <div className="h-2 w-24 bg-brand/20 rounded-full overflow-hidden">
                                <div className="h-full bg-brand" style={{ width: '50%' }}></div>
                            </div>
                            <span className="text-xs font-bold uppercase tracking-wider text-brand">Step 3 of 6</span>
                        </div>
                        <button type="button" className="flex items-center justify-center h-10 w-10 shrink-0 rounded-full bg-brand/10 text-action-black dark:text-white hover:bg-brand/20 transition-colors" aria-label="Close">
                            <X className="shrink-0" size={24} />
                        </button>
                    </header>

                    <main className="flex-1 px-[var(--ds-space-6)] py-[var(--ds-space-6)] lg:px-[var(--ds-space-12)] lg:py-[var(--ds-space-12)] max-w-xl mx-auto w-full flex flex-col justify-center pb-10 lg:pb-[var(--ds-space-12)]">
                        <div className="mb-[var(--ds-space-8)]">
                            <Heading variant="h1" className="ds-heading-page-title text-slate-900 dark:text-slate-100 mb-[var(--ds-space-2)]">
                                Your Photos
                            </Heading>
                            <Text as="p" className="ds-text-page-subtitle text-slate-500 dark:text-slate-400">Upload at least 3 photos to complete your profile.</Text>
                        </div>

                        {duplicateImageError && (
                            <div className="mb-[var(--ds-space-4)] p-3 rounded-lg bg-red-100 border border-red-300 text-red-700 text-sm">
                                Please add an image you haven't already used.
                            </div>
                        )}

                        {/* Grid Layout */}
                        <div className="grid grid-cols-3 gap-3 lg:gap-4 mb-[var(--ds-space-6)]">
                            {/* Slot 1 (Main) */}
                            <div 
                                className="aspect-[3/4] relative group cursor-pointer rounded-[1rem] border-[2px] border-dashed border-brand/40 hover:border-brand transition-colors flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-800/50 shadow-sm overflow-hidden"
                                onClick={() => handleClick("main")}
                            >
                                {renderImageContent(images.main, "Main Photo")}
                                <input
                                    type="file"
                                    ref={fileInputRefs.main}
                                    onChange={(e) => handleFileChange(e, "main")}
                                    style={{ display: "none" }}
                                    accept="image/jpeg,image/jpg,image/png,image/webp"
                                />
                            </div>
                            {/* Slot 2 */}
                            <div 
                                className="aspect-[3/4] relative group cursor-pointer rounded-[1rem] border-[2px] border-dashed border-brand/40 hover:border-brand transition-colors flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-800/50 shadow-sm overflow-hidden"
                                onClick={() => handleClick("first")}
                            >
                                {renderImageContent(images.first, "Second")}
                                <input
                                    type="file"
                                    ref={fileInputRefs.first}
                                    onChange={(e) => handleFileChange(e, "first")}
                                    style={{ display: "none" }}
                                    accept="image/jpeg,image/jpg,image/png,image/webp"
                                />
                            </div>
                            {/* Slot 3 */}
                            <div 
                                className="aspect-[3/4] relative group cursor-pointer rounded-[1rem] border-[2px] border-dashed border-brand/40 hover:border-brand transition-colors flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-800/50 shadow-sm overflow-hidden"
                                onClick={() => handleClick("second")}
                            >
                                {renderImageContent(images.second, "Third")}
                                <input
                                    type="file"
                                    ref={fileInputRefs.second}
                                    onChange={(e) => handleFileChange(e, "second")}
                                    style={{ display: "none" }}
                                    accept="image/jpeg,image/jpg,image/png,image/webp"
                                />
                            </div>                            
                        </div>

                        {/* Mobile Tips Box */}
                        <div className="lg:hidden mb-[var(--ds-space-6)] p-3.5 rounded-[1rem] bg-brand/10 border border-brand/20">
                            <div className="flex items-center gap-2 mb-2.5 text-brand">
                                <Lightbulb className="w-4 h-4 shrink-0 text-brand" />
                                <Text as="span" className="text-[11px] font-bold uppercase tracking-widest text-brand">Expert Advice</Text>
                            </div>
                            <ul className="space-y-1">
                                <li className="flex items-center gap-2 overflow-hidden">
                                    <Sun className="w-3.5 h-3.5 text-brand shrink-0" />
                                    <Text as="span" className="text-[13px] text-slate-700 dark:text-slate-300 whitespace-nowrap overflow-hidden text-ellipsis">Use natural daylight. Avoid dark rooms.</Text>
                                </li>
                                <li className="flex items-center gap-2 overflow-hidden">
                                    <Smile className="w-3.5 h-3.5 text-brand shrink-0" />
                                    <Text as="span" className="text-[13px] text-slate-700 dark:text-slate-300 whitespace-nowrap overflow-hidden text-ellipsis">A warm smile makes you approachable.</Text>
                                </li>
                                <li className="flex items-center gap-2 overflow-hidden">
                                    <CameraOff className="w-3.5 h-3.5 text-brand shrink-0" />
                                    <Text as="span" className="text-[13px] text-slate-700 dark:text-slate-300 whitespace-nowrap overflow-hidden text-ellipsis">Avoid heavy filters. Keep it natural.</Text>
                                </li>
                            </ul>
                        </div>

                        {/* Action Button */}
                        {/* Sticky CTA for Mobile */}
                        <div className="fixed bottom-0 left-0 right-0 z-[40] px-[var(--ds-space-6)] py-4 bg-white/90 dark:bg-background-dark/95 backdrop-blur-lg border-t border-slate-200 dark:border-slate-800 lg:static lg:bg-transparent lg:backdrop-blur-none lg:p-0 lg:border-none lg:z-auto lg:mt-[var(--ds-space-6)] lg:pt-[var(--ds-space-2)]">
                            <DSButton 
                                type="button"
                                variant="cta"
                                onClick={handleNextClick}
                                endIcon={!loading && <ArrowRight size={20} />}
                                disabled={loading}
                                className="w-full"
                            >
                                {loading ? <Spinner animation="border" size="sm" /> : "Continue"}
                            </DSButton>
                        </div>
                    </main>

                    {/* Footer Meta */}
                    <footer className="mt-auto pt-6 lg:pt-auto p-[var(--ds-space-4)] lg:p-[var(--ds-space-6)] lg:pb-[var(--ds-space-6)] pb-32 lg:pb-[var(--ds-space-6)] text-center shrink-0">
                        <Text as="p" className="ds-text-disclaimer text-slate-400">By continuing, you agree to our Terms of Service and Privacy Policy.</Text>
                    </footer>
                </div>
            </div>
            <ImageCrop
                ref={cropperRef}
                show={showCropModal}
                onHide={handleCropCancel}
                url={imageToCrop}
                cropWidth={width}
                cropHeight={height}
                onCropSave={handleCropSave}
                onCropCancel={handleCropCancel}
            />

            {/* Inject minimal scrollbar styles for this component */}
            <style dangerouslySetInnerHTML={{__html: `
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #ff794d33; border-radius: 10px; }
            `}} />
        </div>
    );
};
